import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const initialToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

/**
 * The API client, fetched the first time a request is actually made.
 *
 * This module is imported by AuthProvider, which wraps the entire app — so a
 * static import put axios (38 KB, plus this project's 11 KB api surface) into
 * the main bundle that every visitor downloads, including one reading the
 * marketing page who never calls the API. An anonymous visitor now never
 * fetches it at all.
 *
 * The promise is cached, so concurrent callers share one request and later
 * calls resolve immediately.
 */
let apiPromise;
const getApi = () => {
  if (!apiPromise) apiPromise = import('../utils/api').then((m) => m.default);
  return apiPromise;
};

/**
 * Storage is now the single source of truth for the token: utils/api reads it
 * on every request via an interceptor, so there is no axios header to keep in
 * step here — which is what lets this module avoid importing axios at all.
 */
const writeAuthToken = (newToken) => {
  if (newToken) localStorage.setItem('token', newToken);
  else localStorage.removeItem('token');
};

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation('notifications');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(initialToken);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAndSetUser = useCallback(async (activeToken) => {
    writeAuthToken(activeToken);
    try {
      const axios = await getApi();
      const res = await axios.get('/api/auth/me');
      // /me now returns { success, data: { user, role } } — unwrap. Fall back
      // to the legacy shape (raw user) so this stays safe if the route changes back.
      const u = res.data?.data?.user || res.data;
      setToken(activeToken);
      setUser(u);
      setIsAuthenticated(true);
      return true;
    } catch (err) {
      console.error('Error loading user:', err.response?.data?.msg || err.message);
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      writeAuthToken(null);
      return false;
    }
  }, []);

  // There is deliberately no self-service `register` (join-by-code) here —
  // see server/routes/auth.js's comment above where POST /register used to
  // live. Staff accounts are created by an organization admin, never
  // self-created by the staff member. `registerOrganization` below is the
  // only account-creation path left: a new business registering itself.
  const registerOrganization = useCallback(async (orgData) => {
    try {
      const axios = await getApi();
      const orgRes = await axios.post('/api/organizations', orgData);
      await fetchAndSetUser(orgRes.data.token);
      return { success: true, data: orgRes.data };
    } catch (err) {
      const errorMsg = err.response?.data?.msg || 'Organization registration failed';
      return { success: false, error: errorMsg, data: err?.response?.data };
    }
  }, [fetchAndSetUser]);

  const login = useCallback(async (email, password) => {
    try {
      const axios = await getApi();
      const res = await axios.post('/api/auth/login', { email, password });
      await fetchAndSetUser(res.data.token);
      toast.success(t('auth.loggedIn'));
      return { success: true, data: res.data };
    } catch (err) {
      const errorMsg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.msg || 'Invalid credentials';
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [fetchAndSetUser, t]);

  const loginWithToken = useCallback(async (newToken) => {
    const ok = await fetchAndSetUser(newToken);
    if (ok) toast.success(t('auth.loggedIn'));
    else toast.error(t('auth.authFailed'));
    return { success: ok };
  }, [fetchAndSetUser, t]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    writeAuthToken(null);
    toast.info(t('auth.loggedOut'));
  }, [t]);

  // updateProfile and changePassword return { success, error } and show
  // nothing themselves: UserProfile, their only caller, reports the outcome in
  // the shared Success/Failure popups, and a toast on top would say the same
  // thing twice.
  const updateProfile = useCallback(async (userData) => {
    try {
      const axios = await getApi();
      const res = await axios.put(`/api/users/${user._id}`, userData);
      setUser(res.data);
      return { success: true, data: res.data };
    } catch (err) {
      const errorMsg = err.response?.data?.msg || 'Failed to update profile';
      return { success: false, error: errorMsg };
    }
  }, [user?._id]);

  // UserProfile has always destructured `changePassword` from this context, but
  // it was never provided — submitting the change-password dialog threw
  // "changePassword is not a function" and the password was never sent.
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const axios = await getApi();
      await axios.put('/api/users/password', { currentPassword, newPassword });
      return { success: true };
    } catch (err) {
      const errorMsg =
        err.response?.data?.msg ||
        err.response?.data?.errors?.[0]?.msg ||
        'Failed to update password';
      return { success: false, error: errorMsg };
    }
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      fetchAndSetUser(storedToken).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchAndSetUser]);

  const value = useMemo(() => ({
    token,
    isAuthenticated,
    loading,
    user,
    registerOrganization,
    login,
    loginWithToken,
    logout,
    updateProfile,
    changePassword,
  }), [token, isAuthenticated, loading, user, registerOrganization, login, loginWithToken, logout, updateProfile, changePassword]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
